"use client";

import { memo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Calendar, Trophy, DollarSign, Globe, ShieldCheck, Users, AlertTriangle } from "lucide-react";

const formatRegion = (region: string): string => {
  return region.replace(/(\d+)$/, "").toUpperCase() || region;
};

import { Link } from "@/i18n/navigation";
import { Tournament } from "@/entities/tournament/model/types";
import { getTournamentDisplayPrizeLabel } from "@/entities/tournament/lib/prize-display";
import { useOrganizationRestriction } from "@/hooks/use-organization-restriction";
import { TournamentStatusBadge } from "@/entities/tournament/ui/tournament-status-badge";
import { getBannerUrl } from "@/shared/lib/image-utils";
import { useCountdown } from "@/shared/lib/use-countdown";
import { formatDateOnlyMsk } from "@/shared/lib/msk-time";
import { formatMinRequiredRankRu } from "@/shared/lib/riot-rank";
import { Button } from "@/shared/ui/buttons/button";
import { Card } from "@/shared/ui/cards/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { SafeBanner } from "@/shared/ui/data-display/safe-banner";
import { Progress } from "@/shared/ui/progress";
import { formatNumberUi, toUiLocale } from "@/shared/lib/ui-locale";

interface TournamentInfoRowProps {
  tournament: Tournament;
  variant?: "full" | "compact";
}

const StatusDisplay = memo(({ tournament }: { tournament: Tournament }) => (
  <TournamentStatusBadge tournament={tournament} />
));

export const TournamentInfoRow = memo(function TournamentInfoRow({
  tournament,
  variant = "full",
}: TournamentInfoRowProps) {
  const tUi = useTranslations("Ui");
  const locale = toUiLocale(useLocale());
  const organizationId = tournament.organizationId ?? undefined;
  const { isRestricted } = useOrganizationRestriction(organizationId);
  const canRegister =
    !isRestricted &&
    (tournament.status === "Upcoming" ||
      tournament.status === "RegistrationOpen");

  const prizeLabel = getTournamentDisplayPrizeLabel(
    tournament.prizePool,
    tournament.prizeDistribution,
  );
  const prize =
    prizeLabel.type === "prizes"
      ? [prizeLabel.prizeTypeLabel, prizeLabel.placesRange].filter(Boolean).join(", ")
      : `${formatNumberUi(prizeLabel.value, undefined, locale)} ${tournament.currency === "USD" ? "$" : "₽"}`;
  const formattedDate = formatDateOnlyMsk(
    tournament.startDate ?? new Date().toISOString(),
  );
  const countdown = useCountdown(
    tournament?.startDate || new Date().toISOString(),
  );

  const minRequiredRankRu = formatMinRequiredRankRu(
    tournament.minRequiredRank,
  );

  const organizer = tournament.organizer;
  const organizerHref = organizer
    ? `/organizations/${organizer.url ?? organizer.id}`
    : undefined;

  const statItem = (
    label: string,
    value: React.ReactNode,
    icon: React.ReactNode,
  ) => (
    <div key={label} className="flex items-center gap-2 min-w-0 shrink-0 max-w-[200px] md:max-w-[180px]">
      {icon}
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="text-xs text-white/80">{label}</div>
        <span className="font-semibold text-white truncate block text-sm" title={typeof value === "string" ? value : undefined}>{value}</span>
      </div>
    </div>
  );

  const statsRow = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-6 sm:gap-y-3 text-sm w-full">
      {tournament.type === "Draft" ? (
        // Драфт: участники в пул (без лимита команд) + отдельно число команд.
        <>
          {statItem(
            tUi("tournamentInfoRow.uchastniki"),
            String(tournament.registeredPlayersCount ?? 0),
            <Users className="h-4 w-4 text-blue-400 shrink-0" />,
          )}
          {statItem(
            tUi("litTournamentInfoRow.komand"),
            String(tournament.maxTeamsInDraft ?? "—"),
            <Users className="h-4 w-4 text-blue-400 shrink-0" />,
          )}
        </>
      ) : (
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          <Users className="h-4 w-4 text-blue-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-white/80">{tUi("tournamentInfoRow.uchastniki")}</div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white tabular-nums text-sm">
                {tournament.currentTeams}/{tournament.maxTeams}
              </span>
              <Progress
                value={
                  ((tournament.currentTeams ?? 0) /
                    (tournament.maxTeams || 1)) *
                  100
                }
                className="h-1.5 w-16 shrink-0"
              />
            </div>
          </div>
        </div>
      )}
      {statItem(tUi("litTournamentInfoRow.prizovye"), prize, <DollarSign className="h-4 w-4 text-amber-400 shrink-0" />)}
      {/* Победитель есть только у завершённых — в списке архива это главное,
          ради чего туда заходят, а до правки строка про него молчала. */}
      {tournament.winner?.name &&
        statItem(tUi("litTournamentInfoRow.pobeditel"), tournament.winner.name, <Trophy className="h-4 w-4 text-amber-300 shrink-0" />)}
      {statItem(tUi("litTournamentInfoRow.data"), formattedDate, <Calendar className="h-4 w-4 text-emerald-400 shrink-0" />)}
      {statItem(tUi("litTournamentInfoRow.format"), tournament.format, <Trophy className="h-4 w-4 text-violet-400 shrink-0" />)}
      {tournament.region &&
        statItem(tUi("litTournamentInfoRow.region"), formatRegion(tournament.region), <Globe className="h-4 w-4 text-cyan-400 shrink-0" />)}
      {minRequiredRankRu &&
        statItem(tUi("litTournamentInfoRow.minRang"), minRequiredRankRu, <ShieldCheck className="h-4 w-4 text-teal-400 shrink-0" />)}
    </div>
  );

  const cardContent = (
    <>
      <div className="absolute inset-0 z-0 overflow-hidden">
        <SafeBanner
          src={getBannerUrl(tournament.image)}
          alt={tournament.name}
          fill
          rounded="rounded-none"
        />
        <div className="absolute inset-0 bg-black/60 dark:bg-black/70" />
      </div>

      <div className="relative z-10 flex w-full min-w-0 flex-1 flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="min-w-0 shrink-0 md:w-[200px] lg:w-[240px] md:flex md:flex-col md:justify-center">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <h3 className="text-base sm:text-lg font-bold line-clamp-1 text-white min-w-0 flex-1">
              {tournament.name}
            </h3>
            <div className="shrink-0">
              <StatusDisplay tournament={tournament} />
            </div>
          </div>
          {organizer && organizerHref && (
            <>
              {variant === "compact" ? (
                <span className="mt-1 inline-flex items-center gap-2 min-w-0 text-sm text-white/80">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarImage
                      src={organizer.avatar || ""}
                      alt={organizer.name || "Organizer"}
                    />
                    <AvatarFallback>
                      {(organizer.name || "").charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="line-clamp-1">{organizer.name}</span>
                </span>
              ) : (
                <Link
                  href={organizerHref}
                  className="relative z-20 mt-1 inline-flex w-fit max-w-full items-center gap-2 min-w-0 text-sm text-white/80 hover:text-white transition-colors"
                >
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarImage
                      src={organizer.avatar || ""}
                      alt={organizer.name || "Organizer"}
                    />
                    <AvatarFallback>
                      {(organizer.name || "").charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="line-clamp-1">{organizer.name}</span>
                </Link>
              )}
            </>
          )}
          {variant === "full" && (
            <p className="text-sm text-white/80 mt-1.5 line-clamp-2 overflow-hidden text-ellipsis">
              {tournament.description}
            </p>
          )}
        </div>

        {variant === "full" && (
          <div className="flex-1 min-w-0 flex items-center justify-center">
            {statsRow}
          </div>
        )}

        {variant === "full" && (
          <div className="shrink-0 w-full md:w-44 lg:w-48 p-4 md:py-4 md:flex md:flex-col md:items-center md:justify-center relative z-20">
            {isRestricted && (
              <div className="mb-2 p-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-md flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
                <p className="text-xs text-orange-800 dark:text-orange-200">
                  {tUi("tournamentInfoRow.etaOrganizatsiyaOgranichilaVam")}
                </p>
              </div>
            )}
            <div className="flex gap-2 md:flex-col md:items-center md:justify-center md:w-full md:max-w-[180px]">
              <Button asChild variant="default" size="default" className="flex-1 md:w-full">
                <Link href={`/tournaments/${tournament.slug || tournament.id}`}>
                  {tUi("tournamentInfoRow.podrobnee")}
                </Link>
              </Button>
              {canRegister && (
                <Button
                  asChild
                  variant="outline"
                  size="default"
                  className="flex-1 md:w-full border-white/40 bg-white/10 text-white hover:bg-white/20 hover:border-white/50"
                >
                  <Link
                    href={`/tournaments/${tournament.slug || tournament.id}/register`}
                  >
                    {tUi("tournamentInfoRow.uchastvovat")}
                  </Link>
                </Button>
              )}
              {isRestricted && (
                <Button variant="secondary" size="default" className="flex-1 md:w-full" disabled>
                  {tUi("tournamentInfoRow.uchastvovat")}
                </Button>
              )}
            </div>
            {canRegister && (
              <p className="mt-2 text-center text-xs text-white/80">
                {countdown.days}д {countdown.hours}ч {countdown.minutes}м
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );

  // KAN-671: строка турнира открывает турнир — в обоих вариантах.
  //
  // Оверлей вместо обёртки <Link> вокруг всей карточки: внутри есть ссылка на
  // организатора и кнопки «Подробнее»/«Участвовать», и в compact они оказывались
  // вложенными <a> в <a> — невалидная разметка и сломанная навигация с клавиатуры.
  // Оверлей лежит ниже интерактива (z-[1] против z-20), поэтому вложенные ссылки
  // работают сами, а клик по пустому месту ведёт в турнир.
  const overlayLink = (
    <Link
      href={`/tournaments/${tournament.slug || tournament.id}`}
      aria-label={tournament.name}
      className="absolute inset-0 z-[1] rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );

  if (variant === "compact") {
    return (
      <Card className="group relative gap-0 py-0 p-0 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-primary flex flex-col md:flex-row items-stretch">
        {overlayLink}
        {cardContent}
      </Card>
    );
  }

  return (
    <Card className="relative gap-0 py-0 p-0 overflow-hidden hover:shadow-lg transition-shadow flex flex-col md:flex-row items-stretch">
      {overlayLink}
      {cardContent}
    </Card>
  );
});
